import mapActions from './mapActions.js'
import axios from 'axios'
import router from '../router'
import firebase from 'firebase/compat/app'
import jenks from 'turf-jenks'
const turf = require('@turf/turf')
const fb = require('../firebaseConfig.js')
const { buildFilterExpression } = require('./filterExpression')

// Live unsubscribe for the current farm's onSnapshot listener. fetchFarm used
// to add a new listener every time without removing the old one, so listeners
// for previously-viewed farms stayed alive — and when one of them re-fired it
// would re-resolve state.role against the WRONG farm (clobbering a simulated
// Administrator back to the user's real role on that other farm). We now tear
// down the previous listener before subscribing to a new farm.
let farmUnsubscribe = null

/**
 * Calculate the area of a polygon geometry
 * Uses the Shoelace formula for polygon area calculation
 */
function calculatePolygonArea (geometry) {
  if (!geometry || !geometry.coordinates || !geometry.coordinates[0]) {
    return 0
  }
  
  const coords = geometry.coordinates[0] // Outer ring
  let area = 0
  
  // Shoelace formula for polygon area
  for (let i = 0; i < coords.length - 1; i++) {
    const [x1, y1] = coords[i]
    const [x2, y2] = coords[i + 1]
    area += (x1 * y2) - (x2 * y1)
  }
  
  return Math.abs(area / 2)
}

export default {
  ...mapActions,
  removeBlockHighlight ({ commit, state }) {
    const map = state.mapObject
    if (map.getLayer('blockHighlightLayer')) {
      map.removeLayer('blockHighlightLayer')
      map.removeSource('blockHighlightSource')
    }
  },
  highlightBlock ({ commit, state }, block) {
    const map = state.mapObject
    if (map.getLayer('blockHighlightLayer')) {
      map.removeLayer('blockHighlightLayer')
    }
    if (map.getSource('blockHighlightSource')) {
      map.removeSource('blockHighlightSource')
    }
    map.addSource('blockHighlightSource', {
      type: 'geojson',
      data: block
    })
    // Highlight Layer
    map.addLayer({
      id: 'blockHighlightLayer',
      type: 'fill',
      source: 'blockHighlightSource',
      layout: {},
      paint: {
        'fill-color': '#ffc107', // eslint-disable-line
        'fill-opacity': 0.8 // eslint-disable-line
      }
    })
  },
  highlightDatapoint ({ commit, state }, datapoint) {
    const map = state.mapObject
    if (map.getLayer('datapointHighlightLayer')) {
      map.removeLayer('datapointHighlightLayer')
    }
    if (map.getLayer('datapointHighlightLineLayer')) {
      map.removeLayer('datapointHighlightLineLayer')
    }
    if (map.getLayer('datapointHighlightPolygonLayer')) {
      map.removeLayer('datapointHighlightPolygonLayer')
    }
    if (map.getSource('datapointHighlightSource')) {
      map.removeSource('datapointHighlightSource')
    }
    map.addSource('datapointHighlightSource', {
      type: 'geojson',
      data: datapoint
    })
    const geometry = datapoint && datapoint.geometry
    const geomType = geometry && geometry.type
    if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
      map.addLayer({
        id: 'datapointHighlightPolygonLayer',
        type: 'fill',
        source: 'datapointHighlightSource',
        layout: {},
        paint: {
          'fill-color': '#ffc107',
          'fill-opacity': 0.55,
          'fill-outline-color': '#ffc107'
        }
      })
      return
    }
    if (geomType === 'LineString' || geomType === 'MultiLineString') {
      map.addLayer({
        id: 'datapointHighlightLineLayer',
        type: 'line',
        source: 'datapointHighlightSource',
        layout: {},
        paint: {
          'line-width': (state.dataset.vizSettings?.lineWidth || 2) + 2,
          'line-color': '#ffc107',
          'line-opacity': 0.95
        }
      })
      return
    }
    // Point highlight layer
    map.addLayer({
      id: 'datapointHighlightLayer',
      type: 'circle',
      source: 'datapointHighlightSource',
      layout: {},
      paint: {
        'circle-radius': state.dataset.vizSettings.circleRadius + 4,
        'circle-color': '#ffc107',
        'circle-opacity': 0.8
      }
    })
  },
  refreshFarm ({ commit, state, dispatch }) {
    const pathToGo = '/f/' + state.farm.id
    if (window.location.pathname !== pathToGo) {
      router.push('/f/' + state.farm.id)
    }
    dispatch('initFarmMap')
    // commit('resetDrawers')
    commit('hideAllDrawers')
  },
  initFarmViz ({ commit, state, dispatch }) {
    commit('setStateProperty', { property: 'showKey', value: false })
    // Update the look of the map
    const mapObject = state.mapObject
    const vizSettings = state.farm.vizSettings || false
    // Live Activity layer (docs/live-tracking-phase1.md): honor the persisted
    // toggle on farm load / viz refresh. subscribeLiveTracks is idempotent,
    // but only attach when not already live so routine viz tweaks don't
    // churn the listeners.
    if (vizSettings && vizSettings.showLiveTracks && !state.liveTracksVisible) {
      dispatch('subscribeLiveTracks')
    }
    const colorOpacity = vizSettings.colorOpacity ?? 0.8
    let color
    if (vizSettings) {
      mapObject.setPaintProperty('blocksLayer', 'fill-opacity', colorOpacity)
      if (vizSettings.colorBy === 'solid') {
        mapObject.setPaintProperty('blocksLayer', 'fill-color', vizSettings.blockColor)
      } else if (vizSettings.colorBy === 'valueBased') {
        const minMax = [null, null]
        const colorStops = []
        if (vizSettings.colorHeader) {
          if (vizSettings.colorHeader.type === 'Number' || vizSettings.colorHeader.type === 'Date and Time') {
            state.blocksGeoJson.features.forEach(blockFeature => {
              // Push out the mins and maxes
              if (!minMax[0] && blockFeature.properties[vizSettings.colorHeader.label]) {
                minMax[0] = blockFeature.properties[vizSettings.colorHeader.label]
              }
              if (!minMax[1] && blockFeature.properties[vizSettings.colorHeader.label]) {
                minMax[1] = blockFeature.properties[vizSettings.colorHeader.label]
              }
              if (blockFeature.properties[vizSettings.colorHeader.label] < minMax[0] && blockFeature.properties[vizSettings.colorHeader.label]) {
                minMax[0] = blockFeature.properties[vizSettings.colorHeader.label]
              } else if (blockFeature.properties[vizSettings.colorHeader.label] > minMax[1] && blockFeature.properties[vizSettings.colorHeader.label]) {
                minMax[1] = blockFeature.properties[vizSettings.colorHeader.label]
              }
            })
            vizSettings.minValue = minMax[0]
            vizSettings.maxValue = minMax[1]
            if (vizSettings.zoneClassification === 'linear') {
              // Interpolate requires strictly ascending input values; use single color when min >= max (e.g. one block)
              if (minMax[0] != null && minMax[1] != null && minMax[0] < minMax[1]) {
                color = [
                  'interpolate',
                  ['linear'],
                  ['to-number', ['get', vizSettings.colorHeader.label]],
                  minMax[0],
                  vizSettings.minColor,
                  minMax[1],
                  vizSettings.maxColor
                ]
              } else {
                color = vizSettings.minColor || vizSettings.blockColor || '#6e59c7'
              }
            } else if (vizSettings.zoneClassification === 'equal-interval') {
              const stopDiff = minMax[1] - minMax[0]
              const stopLength = 1 / vizSettings.zoneStops
              for (let i = 1; i <= vizSettings.zoneStops; i++) {
                const stopMultiplier = stopLength * i
                const stopFormula = (stopDiff * stopMultiplier) + minMax[0]
                colorStops.push(stopFormula)
                colorStops.push(vizSettings.colors[i])
              }
              color = [
                'step',
                ['to-number', ['get', vizSettings.colorHeader.label]],
                'black',
                minMax[0],
                vizSettings.colors[0]
              ]
              // Don't push the last 2 values because they only show the absolute last value of the breaks
              colorStops.forEach((stopItem, i) => {
                if (i < colorStops.length - 2) {
                  color.push(stopItem)
                }
              })
            } else if (vizSettings.zoneClassification === 'jenks') {
              const breaks = jenks(state.blocksGeoJson, vizSettings.colorHeader.label, vizSettings.zoneStops)
              vizSettings.breaks = breaks
              if (breaks[0] === undefined) {
                breaks[0] = minMax[0]
              }
              breaks.forEach((breakItem, i) => {
                if (typeof breakItem === 'string') {
                  breaks[i] = Number(breaks[i])
                }
              })
              color = [
                'step',
                ['to-number', ['get', vizSettings.colorHeader.label]],
                'black'
              ]
              breaks.forEach((breakItem, i) => {
                // Don't push the last 2 values because they only show the absolute last value of the breaks
                if (i < breaks.length - 1) {
                  color.push(breaks[i])
                  color.push(vizSettings.colors[i])
                }
              })
            } else if (vizSettings.zoneClassification === 'categorical') {
              // Deal with Categorical Numbers
              const numbersObj = {}
              state.blocksGeoJson.features.forEach(blockFeature => {
                const featureValue = blockFeature.properties[vizSettings.colorHeader.label]
                if (!numbersObj[featureValue]) {
                  numbersObj[featureValue] = true
                }
              })
              const numbersArray = Object.keys(numbersObj)
              numbersArray.forEach(numberString => {
                numberString = Number(numberString)
              })
              const numbersParsedArray = numbersArray.map(Number)
              numbersParsedArray.sort(function (a, b) { return a - b })
              color = [
                'match',
                ['get', vizSettings.colorHeader.label]
              ]
              vizSettings.featureCategories = []
              numbersParsedArray.forEach((category, i) => {
                color.push(category)
                color.push(vizSettings.colors[i])
                vizSettings.featureCategories.push({
                  name: category,
                  count: 0,
                  color: vizSettings.colors[i]
                })
              })
              color.push('#ccc')
              commit('setStateProperty', { property: 'farm', value: state.farm })
            }
          } else if (vizSettings.colorHeader.type === 'Select') {
            // Use label for property key; compiled blocks GeoJSON and AddBlockField/gatherOptions
            // both rely on feature.properties[field.label], so prefer label and fall back to machine_name
            const selectProp = vizSettings.colorHeader.label || vizSettings.colorHeader.machine_name
            color = [
              'match',
              ['get', selectProp],
              'None',
              'black'
            ]
            vizSettings.colorHeader.options.forEach((category, i) => {
              color.push(category)
              color.push(vizSettings.colors[i])
            })
            color.push('#ccc')
          } else if (vizSettings.colorHeader.type === 'Text') {
            const categoryCounts = {}
            let categoryValue
            for (let i = 0; i < state.blocksGeoJson.features.length; i++) {
              categoryValue = state.blocksGeoJson.features[i].properties[vizSettings.colorHeader.label]
              if (typeof categoryCounts[categoryValue] === 'undefined') {
                categoryCounts[categoryValue] = 1
              } else {
                categoryCounts[categoryValue]++
              }
            }
            const farmData = state.farm
            farmData.vizSettings.featureCategories = []
            const entries = Object.entries(categoryCounts)
            entries.forEach((entry, i) => {
              if (i < 62) {
                if (entry[0] === '') {
                  farmData.vizSettings.featureCategories.push({
                    name: 'No Value',
                    count: entry[1],
                    color: '#333'
                  })
                } else {
                  farmData.vizSettings.featureCategories.push({
                    name: entry[0],
                    count: entry[1],
                    color: farmData.vizSettings.colors[i]
                  })
                }
              }
            })
            commit('setStateProperty', { property: 'farm', value: farmData })
            color = [
              'match',
              ['get', farmData.vizSettings.colorHeader.label]
            ]
            farmData.vizSettings.featureCategories.forEach((category, i) => {
              color.push(category.name)
              color.push(category.color)
            })
            color.push('#ccc')
          }
          if (color[3]) {
            mapObject.setPaintProperty('blocksLayer', 'fill-color', color)
          }
        }
      }
      // BLOCK LABEL RENDERING
      if (state.mapObject.getLayer('blocksLabelsLayer')) {
        state.mapObject.removeLayer('blocksLabelsLayer')
      }
      if (state.mapObject.getSource('blocksLabelsLayer')) {
        state.mapObject.removeSource('blocksLabelsLayer')
      }
      const blocksLabelsFeatures = []
      if ((state.blocksGeoJson.features && state.farm.vizSettings.labelBy === 'blockName') || (state.blocksGeoJson.features && state.farm.vizSettings.labelBy === 'headerValue')) {
        let textField
        if (state.farm.vizSettings.labelBy === 'blockName') {
          textField = ['get', 'name']
        } else if (state.farm.vizSettings.labelBy === 'headerValue' && state.farm.vizSettings.labelHeader && state.farm.vizSettings.labelHeader.label) {
          const primaryLabel = state.farm.vizSettings.labelHeader.label
          let primaryLabelSuffix = ''
          if (primaryLabel === 'acres') {
            primaryLabelSuffix = ' acres'
          } else if (primaryLabel === 'hectares') {
            primaryLabelSuffix = ' hectares'
          }
          let secondaryLabel = false
          if (state.farm.vizSettings.secondaryLabelHeader && state.farm.vizSettings.secondaryLabelHeader.label) {
            secondaryLabel = state.farm.vizSettings.secondaryLabelHeader.label
            let secondaryLabelSuffix = ''
            if (secondaryLabel === 'acres') {
              secondaryLabelSuffix = ' acres'
            } else if (secondaryLabel === 'hectares') {
              secondaryLabelSuffix = ' hectares'
            }
            textField = ['concat',
              ['get', primaryLabel],
              primaryLabelSuffix,
              ' - ',
              ['get', secondaryLabel],
              secondaryLabelSuffix
            ]
          } else {
            textField = ['concat',
              ['get', primaryLabel],
              primaryLabelSuffix
            ]
          }
        } else {
          textField = ['get', 'name']
        }
        state.blocksGeoJson.features.forEach(feature => {
          const centroid = turf.centroid(feature, feature.properties)
          blocksLabelsFeatures.push(centroid)
        })

        state.mapObject.addLayer({
          id: 'blocksLabelsLayer',
          type: 'symbol',
          source: {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: blocksLabelsFeatures
            }
          },
          layout: {
            'text-field': textField,
            'text-font': [
              'Open Sans Semibold',
              'Arial Unicode MS Bold'
            ]
          },
          paint: {
            'text-color': '#eee'
          }
        })
      }
    }
    // Apply block filters (show/hide blocks by a field value — numeric range
    // or categorical) to the fill, outline, and label layers. Same expression
    // builder the dataset filters use (store/filterExpression.js). The label
    // centroids carry the block properties, so the same expression filters
    // labels too.
    const blockFilters = buildFilterExpression(vizSettings && vizSettings.filters)
    ;['blocksLayer', 'blocksLayerOutline', 'blocksLabelsLayer'].forEach(id => {
      if (mapObject.getLayer(id)) mapObject.setFilter(id, blockFilters || undefined)
    })
    const farm = state.farm
    farm.vizSettings = vizSettings
    commit('setStateProperty', { property: 'farm', value: farm })
    setTimeout(() => {
      commit('setStateProperty', { property: 'showKey', value: true })
    }, 200)
  },
  renderFarmMap ({ commit, state, dispatch }) {
    state.dataset = {}
    // state.display.showMapSettings = false
    state.viz.header = ''
    state.display.showMap = false
    setTimeout(() => {
      state.display.showMap = true
    }, 100)
    setTimeout(() => {
      mapboxgl.accessToken = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ' // eslint-disable-line
      state.mapObject = new mapboxgl.Map({ // eslint-disable-line
        container: 'map',
        // style: 'mapbox://styles/lergp/ckbb0rqb0074k1iojtfyqy9id',
        style: 'https://api.maptiler.com/maps/da520b6d-739a-4646-888f-1d3f2b664f78/style.json?key=IuJ0D0R9S0Cjl4krhvr1',
        center: [-95, 40],
        zoom: 3
      })
      const navControl = new mapboxgl.NavigationControl({ showCompass: true }) // eslint-disable-line
      commit('setStateProperty', { property: 'zoomControl', value: navControl })
      state.mapObject.addControl(state.zoomControl) // eslint-disable-line

      const scaleControl = new mapboxgl.ScaleControl({ // eslint-disable-line
        maxWidth: 150,
        unit: 'imperial'
      })
      state.mapObject.addControl(scaleControl)

      const geoLocateUser = new mapboxgl.GeolocateControl({ // eslint-disable-line
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true
      })
      commit('setStateProperty', { property: 'locateControl', value: geoLocateUser })
      // Add geolocate control to the map.
      state.mapObject.addControl(state.locateControl)

      geoLocateUser.on('geolocate', function (e) {
        state.userLocation = e.coords
      })

      state.mapObject.on('click', function (e) {
        if (state.drawer.copyDataset.position === 'in') {
          // TAKE NO ACTION WHEN USING THE COPY DATASET FUNCTION
        } else {
          // Remove highlighed block layer
          if (state.mapObject.getLayer('blockHighlightLayer')) {
            state.mapObject.removeLayer('blockHighlightLayer')
          }
          if (state.mapObject.getSource('blockHighlightLayer')) {
            state.mapObject.removeSource('blockHighlightLayer')
          }
          if (state.mapObject.getSource('blockHighlightSource')) {
            state.mapObject.removeSource('blockHighlightSource')
          }
        }
        // state.display.drawer.blockDetails = 'out'
        // commit('hideDrawer', 'blockDetails')
      })

      if (state.farm.blocksGeoJsonPath) {
        state.mapObject.on('load', function () {
          // VARS
          const bounds = new mapboxgl.LngLatBounds() // eslint-disable-line
          // Get blocks geoJson File
          const blocksGeoJsonPath = state.farm.blocksGeoJsonPath
          const blocksGeoJsonStorageRef = firebase.storage().ref(blocksGeoJsonPath)
          blocksGeoJsonStorageRef.getDownloadURL().then(blocksGeoJsonUrl => {
            return axios.get(blocksGeoJsonUrl)
          }).then(response => {
            state.blocksGeoJson = response.data
            
            // Sort blocks by area (largest first) so smaller blocks render on top
            if (state.blocksGeoJson.features && Array.isArray(state.blocksGeoJson.features)) {
              state.blocksGeoJson.features.sort((a, b) => {
                const areaA = calculatePolygonArea(a.geometry)
                const areaB = calculatePolygonArea(b.geometry)
                return areaB - areaA // Descending order (largest first)
              })
              console.log('Sorted blocks by area - largest to smallest for proper layering')
            }
            
            let opacitySetting = 0.8
            if (state.farm.vizSettings && state.farm.vizSettings.colorOpacity != null) {
              opacitySetting = state.farm.vizSettings.colorOpacity
            }
            // Custom raster base layer (NAIP / aerial / saved scene) goes
            // on FIRST so blocks render on top of it. No-op when farm has
            // no baseLayer configured.
            dispatch('addCustomBaseLayer')

            // Named source so we can update data when block log values change
            state.mapObject.addSource('blocksSource', {
              type: 'geojson',
              data: state.blocksGeoJson
            })
            // ADD LAYERS
            state.mapObject.addLayer({
              id: 'blocksLayer',
              type: 'fill',
              source: 'blocksSource',
              layout: {},
              paint: {
                'fill-color': '#6e59c7', // eslint-disable-line
                'fill-opacity': opacitySetting, // eslint-disable-line
              }
            })
            state.mapObject.addLayer({
              id: 'blocksLayerOutline',
              type: 'line',
              source: 'blocksSource',
              layout: {},
              paint: {
                'line-width': 2,
                'line-color': '#eee'
              }
            })
            // CLICK EVENTS
            state.mapObject.on('click', 'blocksLayer', function (e) {
              // AVIRIS-3 point sampling owns the map click while it's
              // active — don't also navigate to the clicked block.
              if (state.avirisSampling) return
              // Look at params to see if we're on the dataset page
              const currentPath = window.location.pathname
              if (currentPath.includes('/dataset/')) {
              } else {
                router.push(`/f/${state.farm.id}/block/${e.features[0].properties.blockId}`)
                // Close drawers
                commit('hideAllDrawers')
                // Add highlighted block layer
                if (state.mapObject.getLayer('blockHighlightLayer')) {
                  state.mapObject.removeLayer('blockHighlightLayer')
                  state.mapObject.removeSource('blockHighlightLayer')
                }
                // When the Satellite panel is open we want to SEE the imagery,
                // so skip the opaque yellow fill and draw a thick border instead.
                const satelliteOpen = state.drawer && state.drawer.satellite && state.drawer.satellite.show
                if (satelliteOpen) {
                  state.mapObject.addLayer({
                    id: 'blockHighlightLayer',
                    type: 'line',
                    source: {
                      type: 'geojson',
                      data: e.features[0]
                    },
                    layout: { 'line-join': 'round', 'line-cap': 'round' },
                    paint: {
                      'line-color': '#ffc107',
                      'line-width': 4,
                      'line-opacity': 0.95
                    }
                  })
                } else {
                  state.mapObject.addLayer({
                    id: 'blockHighlightLayer',
                    type: 'fill',
                    source: {
                      type: 'geojson',
                      data: e.features[0]
                    },
                    layout: {},
                    paint: {
                      'fill-color': '#ffc107',
                      'fill-opacity': 0.8
                    }
                  })
                }
                fb.blocksCollection.doc(e.features[0].properties.blockId).get().then(doc => {
                  state.block = doc.data()
                  state.block.id = doc.id
                  if (!satelliteOpen) {
                    setTimeout(() => {
                      commit('showDrawer', 'blockDetails')
                    }, 300)
                  }
                }).catch(err => {
                  console.log(err.message)
                })
              }
            })
            // MOUSE ENTER AND LEAVE POINTERS
            state.mapObject.on('mouseenter', 'blocksLayer', function () {
              state.mapObject.getCanvas().style.cursor = 'pointer'
            })
            state.mapObject.on('mouseleave', 'blocksLayer', function () {
              state.mapObject.getCanvas().style.cursor = ''
            })
            // BOUNDS UPDATE ONLY IF THERE ARE FEATURES
            if (state.blocksGeoJson.features.length > 0) {
              state.blocksGeoJson.features.forEach(feature => {
                const coordinates = feature.geometry.coordinates
                if (feature.geometry.type === 'Polygon') {
                  // For polygons, coordinates are nested arrays
                  coordinates[0].forEach(coord => {
                    if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                      bounds.extend(coord)
                    }
                  })
                } else if (feature.geometry.type === 'Point') {
                  // For points, coordinates are a single array
                  if (!isNaN(coordinates[0]) && !isNaN(coordinates[1])) {
                    bounds.extend(coordinates)
                  }
                } else if (feature.geometry.type === 'LineString') {
                  coordinates.forEach(coord => {
                    if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                      bounds.extend(coord)
                    }
                  })
                } else if (feature.geometry.type === 'MultiLineString') {
                  coordinates.forEach(line => {
                    line.forEach(coord => {
                      if (!isNaN(coord[0]) && !isNaN(coord[1])) {
                        bounds.extend(coord)
                      }
                    })
                  })
                }
              })
              state.mapObject.fitBounds(bounds, {
                padding: 50,
                animate: false
              })
            } else {
              const lat = state.farm.geolocation.latitude
              const lng = state.farm.geolocation.longitude
              state.mapObject.flyTo({
                center: [lng, lat],
                zoom: 15,
                animate: false
              })
            }
            state.loadingMap = false
            dispatch('initFarmViz')
          }) // axios get blocksGeoJson
        }) // map.on load
      } else {
        if (state.farm.geolocation) {
          const lat = state.farm.geolocation.latitude
          const lng = state.farm.geolocation.longitude
          state.mapObject.flyTo({
            center: [lng, lat],
            zoom: 15,
            animate: false
          })
          state.loadingMap = false
        } else {
          console.log('Farm data taking too long... refresh this farm in 3... 2... 1...')
          setTimeout(() => {
            dispatch('refreshFarm')
          }, 3000)
        }
      }
      commit('resetKey', 'blocks')
    }, 500)
  },
  initFarmMap ({ commit, state, dispatch }) {
    commit('setStateProperty', { property: 'selectBlocksListenersAdded', value: false })
    if (state.loadingMap === false) {
      commit('setStateProperty', { property: 'loadingMap', value: true })
    }
    if (state.farm.recompileBlocks === true) {
      commit('setStateProperty', { property: 'alert', value: { active: true, message: 'Changes have been made to your blocks. Recompiling now...', level: 'alert-info' } })
      fb.compileBlocksRequestsCollection.add({
        complete: false,
        createdBy: state.currentUser.uid,
        createdOn: new Date(),
        farmId: state.farm.id,
        oldGeoJsonPath: state.farm.blocksGeoJsonPath || false
      }).then(doc => {
        const requestId = doc.id
        fb.compileBlocksRequestsCollection.doc(requestId).onSnapshot(updatedDoc => {
          const updatedRequest = updatedDoc.data()
          updatedRequest.id = updatedDoc.id
          if (updatedRequest.complete) {
            fb.farmsCollection.doc(state.farm.id).update({
              recompileBlocks: false,
              updatedBy: state.currentUser.uid,
              updatedOn: new Date()
            }).then(() => {
              return fb.farmsCollection.doc(state.farm.id).get()
            }).then(doc => {
              const updatedFarm = doc.data()
              updatedFarm.id = doc.id
              commit('setStateProperty', { property: 'farm', value: updatedFarm })
              // commit('initFarmMap')
              dispatch('renderFarmMap')
              commit('setStateProperty', { property: 'alert', value: { active: false, message: 'Changes have been made to your blocks. Recompiling now...', level: 'alert-info' } })
            }).catch(err => {
              console.log(err.message)
            })
          }
        })
      })
    } else {
      // commit('initFarmMap')
      dispatch('renderFarmMap')
    }
  },
  initDatasetMap ({ commit, state, dispatch }) {
    // Refresh the dataset map after bulk operations
    // This will re-fetch the dataset data and update the map
    if (state.dataset && state.dataset.id) {
      // Re-fetch the dataset to get updated data
      fb.datasetsCollection.doc(state.dataset.id).get().then(doc => {
        const updatedDataset = doc.data()
        updatedDataset.id = doc.id
        commit('setStateProperty', { property: 'dataset', value: updatedDataset })
        // Re-fetch the dataset GeoJSON
        if (updatedDataset.dynamic && updatedDataset.collectorId) {
          return fb.collectorsCollection.doc(updatedDataset.collectorId).collection('dataPoints').get()
        } else {
          // For static datasets, you might need to re-fetch the GeoJSON file
          return Promise.resolve()
        }
      }).then(snapshot => {
        if (snapshot) {
          const features = []
          snapshot.forEach(doc => {
            const dataPoint = doc.data()
            dataPoint.id = doc.id
            // Convert to GeoJSON feature
            if (dataPoint.location) {
              features.push({
                type: 'Feature',
                properties: {
                  dataPointId: dataPoint.id,
                  ...dataPoint
                },
                geometry: {
                  type: 'Point',
                  coordinates: [dataPoint.location.longitude, dataPoint.location.latitude]
                }
              })
            }
          })
          const datasetGeoJson = {
            type: 'FeatureCollection',
            features: features
          }
          commit('setStateProperty', { property: 'datasetGeoJson', value: datasetGeoJson })
        }
      }).catch(err => {
        console.error('Error refreshing dataset map:', err)
      })
    }
  },
  onCollectorClick ({ commit, state }, e) {
    console.log('Collector Click')
    console.log(state.collector)
    const map = state.mapObject
    const collectorObj = state.collector
    if (state.collector.collecting) {
      if (state.collector.selectMarker != null) {
        state.collector.selectMarker.remove()
      }
      const markerOptions = e
      markerOptions.color = 'orange'
      // markerOptions.draggable = true
      collectorObj.selectMarker = new mapboxgl.Marker(markerOptions) // eslint-disable-line
        .setLngLat(e.lngLat)
        .addTo(map)

      collectorObj.selectedLocation = new firebase.firestore.GeoPoint(e.lngLat.lat, e.lngLat.lng)
      commit('setStateProperty', { property: 'collector', value: collectorObj })
    }
  },
  turnOffCollectorClick ({ commit, state }) {
    console.log('Turn off Collector Click')
    commit('setCollecting', false)
    const map = state.mapObject
    if (map) {
      // Always clear any residual draw cursor state when exiting collection.
      try { map.getCanvas().style.cursor = '' } catch (e) { /* ignore */ }
      try {
        const container = map.getContainer()
        if (container && container.classList) {
          [
            'mode-draw_polygon',
            'mode-draw_line_string',
            'mode-direct_select',
            'mode-simple_select',
            'mouse-add',
            'mouse-move',
            'mouse-pointer',
            'feature-vertex',
            'feature-midpoint'
          ].forEach(cls => container.classList.remove(cls))
        }
      } catch (e) { /* ignore */ }
    }
    if (state.collector.selectMarker != null) {
      state.collector.selectMarker.remove()
    }
    const drawCreateHandler = state.collector.drawCreateHandler
    const collectorClickHandler = state.collector.collectorClickHandler
    if (drawCreateHandler && map) {
      map.off('draw.create', drawCreateHandler)
    }
    if (collectorClickHandler && map) {
      map.off('click', collectorClickHandler)
    }
    const drawControl = state.collector.collectorDrawControl
    if (drawControl && map) {
      try {
        map.removeControl(drawControl)
        if (map._collectorDrawControlRef === drawControl) map._collectorDrawControlRef = null
      } catch (e) { /* ignore */ }
      commit('setStateProperty', {
        property: 'collector',
        value: { ...state.collector, collectorDrawControl: null, selectedGeometry: null, drawCreateHandler: null, collectorClickHandler: null }
      })
    } else if (drawCreateHandler || collectorClickHandler) {
      commit('setStateProperty', {
        property: 'collector',
        value: { ...state.collector, drawCreateHandler: null, collectorClickHandler: null }
      })
    }
  },
  collect ({ commit, state, dispatch }, collector) {
    if (!state.display.editMode) {
      commit('setStateProperty', { property: 'dataPoint', value: {} })
    }
    // Clear any existing point marker when starting a new collect session
    if (state.collector.selectMarker != null) {
      state.collector.selectMarker.remove()
    }
    const collectorObj = { ...collector }
    collectorObj.collecting = true
      collectorObj.selectMarker = null
      collectorObj.selectedGeometry = null
      commit('setStateProperty', { property: 'collector', value: collectorObj })
      const map = state.mapObject
    const geometryType = collector.geometryType || 'point'

    if (geometryType === 'polygon' || geometryType === 'line') {
      const drawGeometryType = geometryType === 'line' ? 'LineString' : 'Polygon'
      const drawMode = geometryType === 'line' ? 'draw_line_string' : 'draw_polygon'
      // Remove point-collector map click handler so draw clicks don't place a pin
      const collectorClickHandler = state.collector.collectorClickHandler
      if (collectorClickHandler && map) {
        map.off('click', collectorClickHandler)
      }
      const editGeometry = collector.editGeometry
      let drawControl = state.collector.collectorDrawControl
      const drawCreateHandler = (e) => {
        if (!e.features || !e.features.length) return
        const dc = state.collector.collectorDrawControl
        if (!dc) return
        const all = dc.getAll()
        let feature = e.features[0]
        // Enforce a single shape by keeping the most recently drawn geometry.
        if (all.features && all.features.length > 1) {
          const keepId = feature.id
          all.features.forEach(f => {
            if (f.id && f.id !== keepId) {
              dc.delete(f.id)
            }
          })
        }
        // After deletions, re-fetch to get the kept feature
        const updated = dc.getAll()
        feature = (updated.features && updated.features.find(f => f.id === (e.features[0] && e.features[0].id))) || feature || (updated.features && updated.features[0])
        const geom = feature && feature.geometry
        const isValidDrawGeometry = geom && (
          (drawGeometryType === 'Polygon' && (geom.type === 'Polygon' || geom.type === 'MultiPolygon')) ||
          (drawGeometryType === 'LineString' && (geom.type === 'LineString' || geom.type === 'MultiLineString'))
        )
        if (feature && isValidDrawGeometry) {
          let representativePoint = turf.centroid(feature)
          if (drawGeometryType === 'LineString') {
            try {
              const totalLengthKm = turf.length(feature, { units: 'kilometers' })
              representativePoint = turf.along(feature, totalLengthKm / 2, { units: 'kilometers' })
            } catch (e) { /* fallback to centroid */ }
          }
          const lat = representativePoint.geometry.coordinates[1]
          const lng = representativePoint.geometry.coordinates[0]
          const c = state.collector
          const payload = {
            ...c,
            selectedGeometry: geom,
            selectedLocation: new firebase.firestore.GeoPoint(lat, lng)
          }
          // Defer commit so Vue reactivity picks up the change after Draw's event cycle
          setTimeout(() => {
            commit('setStateProperty', {
              property: 'collector',
              value: payload
            })
          }, 0)
        }
      }
      if (drawControl) {
        try { drawControl.deleteAll() } catch (e) { /* ignore */ }
        try { map._collectorDrawControlRef = drawControl } catch (e) { /* ignore */ }
        const prevHandler = state.collector.drawCreateHandler
        if (prevHandler) map.off('draw.create', prevHandler)
        map.on('draw.create', drawCreateHandler)
        commit('setStateProperty', {
          property: 'collector',
          value: { ...state.collector, drawCreateHandler, collectorClickHandler: null }
        })
        // For new polygons (not edit), start in draw mode; in edit, user can choose draw if desired
        if (!editGeometry) {
          try { drawControl.changeMode(drawMode) } catch (e2) { /* ignore */ }
        }
      } else {
        drawControl = new MapboxDraw({ // eslint-disable-line
          displayControlsDefault: false,
          controls: geometryType === 'line' ? { line_string: true, trash: true } : { polygon: true, trash: true }
        })
        map.addControl(drawControl)
        collectorObj.collectorDrawControl = drawControl
        try { map._collectorDrawControlRef = drawControl } catch (e) { /* ignore */ }
        commit('setStateProperty', { property: 'collector', value: collectorObj })
        // Always attach draw.create so single-polygon enforcement works in both create and edit flows
        const prevHandler = state.collector.drawCreateHandler
        if (prevHandler) map.off('draw.create', prevHandler)
        map.on('draw.create', drawCreateHandler)
        commit('setStateProperty', {
          property: 'collector',
          value: { ...state.collector, drawCreateHandler, collectorClickHandler: null }
        })
        if (editGeometry) {
          let geom = editGeometry
          if (typeof geom === 'string') {
            try { geom = JSON.parse(geom) } catch (e) { geom = null }
          }
          const isEditableGeometry = geom && (
            (geometryType === 'polygon' && (geom.type === 'Polygon' || geom.type === 'MultiPolygon')) ||
            (geometryType === 'line' && (geom.type === 'LineString' || geom.type === 'MultiLineString'))
          )
          if (isEditableGeometry) {
            const fc = { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: geom }] }
            try { drawControl.add(fc) } catch (e) { /* ignore */ }
          }
        } else {
          // New shape datapoint: start with the draw tool active
          try { drawControl.changeMode(drawMode) } catch (e2) { /* ignore */ }
        }
      }
      setTimeout(() => {
        const polygonBtn = document.querySelector('.mapbox-gl-draw_polygon')
        if (polygonBtn) polygonBtn.setAttribute('title', 'Draw an area on the map')
        const lineBtn = document.querySelector('.mapbox-gl-draw_line') || document.querySelector('.mapbox-gl-draw_line_string')
        if (lineBtn) lineBtn.setAttribute('title', 'Draw a path on the map')
        const trashBtn = document.querySelector('.mapbox-gl-draw_trash')
        if (trashBtn) trashBtn.setAttribute('title', 'Delete drawn shape')
      }, 150)
    } else {
      const collectorClickHandler = (e) => { dispatch('onCollectorClick', e) }
      const prevClickHandler = state.collector.collectorClickHandler
      if (prevClickHandler && map) map.off('click', prevClickHandler)
      map.on('click', collectorClickHandler)
      commit('setStateProperty', {
        property: 'collector',
        value: { ...state.collector, collectorClickHandler }
      })
      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
      if (vw <= 800 && !state.display.editMode) {
        setTimeout(() => {
          const geolocateButton = document.getElementsByClassName('mapboxgl-ctrl-geolocate')
          if (geolocateButton && geolocateButton[0]) geolocateButton[0].click()
        }, 300)
      }
    }
    commit('showDrawer', 'collector')
  },
  clearUserData ({ commit }) {
    commit('setCurrentUser', null)
    commit('setUserProfile', {})
    commit('setStateProperty', { property: 'isSuperAdmin', value: false })
  },
  fetchUserProfile ({ commit, state }) {
    const uid = state.currentUser.uid
    fb.usersCollection.doc(uid).get().then(res => {
      commit('setUserProfile', res.data())
    }).catch(err => {
      console.log(err)
    })
    // Load super-admin flag in parallel. The `admins` Firestore rule
    // allows a user to read THEIR OWN doc; reading anyone else's is
    // denied so the admin list can't be enumerated. Permission-denied
    // is the expected response for a non-admin user — treat it the
    // same as a missing doc (= not an admin).
    fb.db.collection('admins').doc(uid).get().then(async snap => {
      const isAdmin = snap.exists && snap.data().admin === true
      commit('setStateProperty', { property: 'isSuperAdmin', value: isAdmin })
      // Super-admin status is also mirrored into an `admin` auth claim that
      // the Storage/Firestore rules read from the ID token. The claim is set
      // server-side (syncAdminClaim) and only lands on a token refresh, so if
      // this admin's current token is missing it, force a refresh — otherwise
      // their rule evaluations wouldn't see the claim until the token expires.
      if (isAdmin && fb.auth.currentUser) {
        try {
          const tokenResult = await fb.auth.currentUser.getIdTokenResult()
          if (!tokenResult.claims || tokenResult.claims.admin !== true) {
            await fb.auth.currentUser.getIdToken(true)
          }
        } catch (e) {
          // Non-fatal: rules fall back to membership-based access.
          console.log('Admin claim refresh skipped:', e && e.message)
        }
      }
    }).catch(() => {
      commit('setStateProperty', { property: 'isSuperAdmin', value: false })
    })
  },
  fetchFarm ({ commit, state }, farmId) {
    let farm
    // Drop the listener for any previously-viewed farm so its snapshots can't
    // re-fire and overwrite state.role against the wrong farm.
    if (typeof farmUnsubscribe === 'function') {
      farmUnsubscribe()
      farmUnsubscribe = null
    }
    farmUnsubscribe = fb.farmsCollection.doc(farmId).onSnapshot(doc => {
      // Guard against a missing farm (e.g. a stale defaultFarm pointing at a
      // deleted farm): doc.data() is undefined, and the old code crashed on
      // `farm.id = doc.id`. Bail out gracefully instead.
      if (!doc.exists) {
        console.warn('fetchFarm: farm', farmId, 'does not exist; skipping')
        return
      }
      farm = doc.data()
      farm.id = doc.id
      const userId = state.currentUser.uid
      // Super-admin role simulation: if the farm was launched with a
      // simulated role from /admin, apply that instead of the user's real
      // role so the UI gates exactly as that role experiences it. (Kept
      // inside the snapshot callback so a farm-doc update doesn't reset it.)
      //
      // We deliberately do NOT gate this on state.isSuperAdmin: that flag is
      // populated asynchronously by fetchUserProfile's admins-doc read, so if
      // the farm snapshot resolved first the simulated role would silently
      // fall through to the user's real membership. simulation.active can
      // only be set from the admin Farm Manager (behind the /admin route
      // guard), and the simulated role is UI-only — backend rules still
      // enforce real permissions — so trusting it here is safe.
      //
      // 'Owner' has no distinct in-farm gating — real owners resolve to
      // 'Administrator' below — so simulate it the same way.
      const sim = state.simulation
      if (sim && sim.active && sim.role && sim.farmId !== farm.id) {
        console.warn('[simulation] active for farm', sim.farmId, 'but loaded farm is', farm.id, '— simulated role not applied')
      }
      if (sim && sim.active && sim.role && sim.farmId === farm.id) {
        commit('setStateProperty', { property: 'role', value: sim.role === 'Owner' ? 'Administrator' : sim.role })
      } else if (farm.permissions[userId]) {
        commit('setStateProperty', { property: 'role', value: farm.permissions[userId] })
      } else if (farm.ownerId === userId) {
        commit('setStateProperty', { property: 'role', value: 'Administrator' })
      } else {
        commit('setStateProperty', { property: 'role', value: '' })
      }
      // Get farm owner details
      fb.usersCollection.doc(farm.ownerId).get().then(user => {
        farm.ownerData = user.data()
        farm.ownerData.id = user.id
      })
      // Get collaborators' details and push to the farm object
      farm.collaboratorData = []
      for (let i = 0; i < farm.users.length; i++) {
        const user = farm.users[i].id
        fb.usersCollection.doc(user).get().then(user => {
          const userData = user.data()
          userData.id = user.id
          farm.collaboratorData.push(userData)
        })
      }
      return commit('setFarm', farm)
    })
  },
  fetchFarms ({ commit, state }) {
    const userId = state.currentUser.uid
    const farms = []
    // First get farms I own
    return fb.farmsCollection.where('ownerId', '==', userId).orderBy('createdOn', 'desc').limit(300).get().then(snapshots => {
      snapshots.forEach(doc => {
        const item = doc.data()
        item.id = doc.id
        item.role = 'Owner'
        farms.push(item)
      })
      // Then get farms I collaborate on
      return fb.farmsCollection.where('users', 'array-contains', { id: userId }).orderBy('createdOn', 'desc').limit(300).get()
      // return true
    }).then(snapshots => {
      snapshots.forEach(function (doc) {
        const item = doc.data()
        item.id = doc.id
        item.role = item.permissions[userId]
        farms.push(item)
      })
      return commit('setFarms', farms)
    }).catch(err => {
      console.log(err)
    })
  },
  fetchBlock ({ commit, state }, blockId) {
    let block
    fb.blocksCollection.doc(blockId).get().then(doc => {
      block = doc.data()
      block.id = doc.id
      return commit('setBlock', block)
    })
  },
  /**
   * After a block's log (or header value) is updated, sync that block's properties
   * into state.blocksGeoJson and refresh the map so value-based viz updates.
   */
  refreshBlocksVizFromBlock ({ state, dispatch }, { blockId, blockData }) {
    if (!state.blocksGeoJson || !state.blocksGeoJson.features || !blockId || !blockData) return
    const fields = state.farm && state.farm.blockFields ? state.farm.blockFields : []
    let updated = false
    const toSeconds = (v) => {
      if (v == null) return null
      if (typeof v === 'number') return v
      if (v._seconds != null) return v._seconds
      if (v.seconds != null) return v.seconds
      return Math.floor((v.getTime ? v.getTime() : new Date(v).getTime()) / 1000)
    }
    state.blocksGeoJson.features.forEach(feature => {
      if (feature.properties && feature.properties.blockId === blockId) {
        fields.forEach(field => {
          const mn = field.machine_name
          const propName = field.label
          let value = blockData[mn]
          if (value === undefined && blockData.latestLogs && blockData.latestLogs[mn]) {
            value = blockData.latestLogs[mn].value
          }
          if (value !== undefined) {
            if (field.type === 'Date and Time') {
              feature.properties[propName] = toSeconds(value)
            } else {
              feature.properties[propName] = value
            }
            updated = true
          }
        })
      }
    })
    if (updated && state.mapObject) {
      const source = state.mapObject.getSource('blocksSource') || state.mapObject.getSource('blocksLayer')
      if (source) {
        source.setData(JSON.parse(JSON.stringify(state.blocksGeoJson)))
        dispatch('initFarmViz')
      }
    }
  },
  fetchBlocks ({ commit, state }, farmId) {
    const blocks = []
    console.log('fetchBlocks action called with farmId:', farmId)
    return fb.blocksCollection.where('farmId', '==', farmId).get().then(snapshots => {
      console.log('fetchBlocks query returned', snapshots.size, 'documents')
      snapshots.forEach(doc => {
        const item = doc.data()
        item.id = doc.id
        blocks.push(item)
        console.log('Block found:', doc.id, item.name || 'unnamed')
      })
      console.log('Total blocks collected:', blocks.length)
      return commit('setBlocks', blocks)
    }).catch(err => {
      console.error('fetchBlocks error:', err)
    })
  },
  // fetchFarmMaps ({ commit, state }, farmId) {
  //   let order = 'createdOn'
  //   let direction = 'desc'
  //   // const tagFilter = state.filterBy
  //   if (state.sortBy === 'Most Recent') {
  //     order = 'createdOn'
  //     direction = 'desc'
  //   } else if (state.sortBy === 'Oldest') {
  //     order = 'createdOn'
  //     direction = 'asc'
  //   } else if (state.sortBy === 'Name A-Z') {
  //     order = 'name'
  //     direction = 'asc'
  //   } else if (state.sortBy === 'Name Z-A') {
  //     order = 'name'
  //     direction = 'desc'
  //   }
  //   let mapsQuery
  //   if (state.filterBy === '') {
  //     mapsQuery = fb.mapsCollection.where('farmId', '==', farmId).orderBy(order, direction).limit(300)
  //   }
  //   // else {
  //   //   mapsQuery = fb.mapsCollection.where('farmId', '==', farmId).where('tags', 'array-contains', tagFilter).orderBy(order, direction).limit(300)
  //   // }
  //   mapsQuery.onSnapshot(snapshots => {
  //     const maps = []
  //     const tags = []
  //     snapshots.forEach(doc => {
  //       const item = doc.data()
  //       item.id = doc.id
  //       state.blocks.forEach(block => {
  //         if (block.id === doc.blockId) {
  //           item.blockName = block.name
  //           console.log(item.blockName)
  //         }
  //       })
  //       maps.push(item)
  //       if (item.tags) {
  //         item.tags.forEach(tag => {
  //           if (!tags.includes(tag)) {
  //             tags.push(tag)
  //           }
  //         })
  //       }
  //       commit('setTags', tags)
  //     })
  //     return commit('setMaps', maps)
  //   })
  // },
  fetchCollectors ({ commit, state }, farmId) {
    fb.collectorsCollection.where('farmId', '==', farmId).where('trashed', '==', false).orderBy('createdOn', 'desc').limit(300).onSnapshot(snapshots => {
      const collectors = []
      snapshots.forEach(doc => {
        const item = doc.data()
        item.id = doc.id
        collectors.push(item)
      })
      return commit('setCollectors', collectors)
    })
  },
  fetchFolders ({ commit, state }, farmId) {
    fb.foldersCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).orderBy('name', 'asc').onSnapshot(docs => {
      const folders = []
      docs.forEach(doc => {
        const folder = doc.data()
        folder.id = doc.id
        folders.push(folder)
      })
      commit('setStateProperty', { property: 'folders', value: folders })
    })
  },
  fetchDatasets ({ commit, state }, farmId) {
    let order = 'createdOn'
    let direction = 'desc'
    // const tagFilter = state.filterBy
    if (state.paginate === false) {
      commit('setDatasets', [])
    }
    if (state.sortBy === 'Most Recent') {
      order = 'updatedOn'
      direction = 'desc'
    } else if (state.sortBy === 'Oldest') {
      order = 'updatedOn'
      direction = 'asc'
    } else if (state.sortBy === 'Name A-Z') {
      order = 'name'
      direction = 'asc'
    } else if (state.sortBy === 'Name Z-A') {
      order = 'name'
      direction = 'desc'
    }
    let datasetsQuery
    if (state.datasets.length < 1) {
      datasetsQuery = fb.datasetsCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).orderBy(order, direction).limit(30)
      // if (state.filterBy === '') {
      //   datasetsQuery = fb.datasetsCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).orderBy(order, direction).limit(30)
      // } else {
      //   datasetsQuery = fb.datasetsCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).where('tags', 'array-contains', tagFilter).orderBy(order, direction).limit(30)
      // }
    } else {
      const lastVisible = state.lastVisibleDataset
      datasetsQuery = fb.datasetsCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).orderBy(order, direction).startAfter(lastVisible).limit(30)
      // if (state.filterBy === '') {
      //   datasetsQuery = fb.datasetsCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).orderBy(order, direction).startAfter(lastVisible).limit(30)
      // } else {
      //   datasetsQuery = fb.datasetsCollection.where('farmId', '==', farmId).where('folderId', '==', state.folder.id).where('tags', 'array-contains', tagFilter).orderBy(order, direction).startAfter(lastVisible).limit(30)
      // }
    }

    datasetsQuery.onSnapshot(snapshots => {
      if (state.paginate === false) {
        commit('setDatasets', [])
      }
      commit('setStateProperty', { property: 'lastVisibleDataset', value: snapshots.docs[snapshots.docs.length - 1] })
      const datasets = state.datasets
      // const datasetTags = state.datasetTags
      snapshots.forEach(doc => {
        const item = doc.data()
        item.id = doc.id
        datasets.push(item)
      })
      commit('setStateProperty', { property: 'paginate', value: false })
      // commit('setDatasetTags', datasetTags)
      return commit('setDatasets', datasets)
    })
  }
  // fetchMaps ({ commit, state }, farmId) {
  //   let order = 'createdOn'
  //   let direction = 'desc'
  //   const tagFilter = state.filterBy
  //   if (state.sortBy === 'Most Recent') {
  //     order = 'createdOn'
  //     direction = 'desc'
  //   } else if (state.sortBy === 'Oldest') {
  //     order = 'createdOn'
  //     direction = 'asc'
  //   } else if (state.sortBy === 'Name A-Z') {
  //     order = 'name'
  //     direction = 'asc'
  //   } else if (state.sortBy === 'Name Z-A') {
  //     order = 'name'
  //     direction = 'desc'
  //   }
  //   let mapsQuery
  //   if (state.filterBy === '') {
  //     mapsQuery = fb.mapsCollection.where('farmId', '==', farmId).orderBy(order, direction).limit(300)
  //   } else {
  //     mapsQuery = fb.mapsCollection.where('farmId', '==', farmId).where('tags', 'array-contains', tagFilter).orderBy(order, direction).limit(300)
  //   }
  //   mapsQuery.onSnapshot(snapshots => {
  //     const maps = []
  //     const tags = []
  //     snapshots.forEach(doc => {
  //       const item = doc.data()
  //       item.id = doc.id
  //       maps.push(item)
  //       if (item.tags) {
  //         item.tags.forEach(tag => {
  //           if (!tags.includes(tag)) {
  //             tags.push(tag)
  //           }
  //         })
  //       }
  //       commit('setTags', tags)
  //     })
  //     return commit('setMaps', maps)
  //   })
  // }
}
